/**
 * Pinterest Ads — Async Report Templates HTTP lifecycle (Story 19.5, Task 2).
 *
 * Pinterest's async report templates are POST→Poll→Download:
 *   1. Create:   POST /v5/ad_accounts/{id}/reports → { token }
 *   2. Poll:     GET  /v5/ad_accounts/{id}/reports?token=… → { report_status, url? }
 *   3. Download: GET  <signed S3 URL>  (5-min validity, no auth header)
 *
 * Used when callers need >30 columns or a `level` not exposed via the sync
 * `/analytics` endpoints (PIN_PROMOTION, PRODUCT_GROUP, PRODUCT_ITEM,
 * KEYWORD). Sync analytics (Tasks 1) covers the typical agency-sized
 * fetches at ≤30 columns / level ≤ AD.
 *
 * Pinterest's JSON download is plain `application/json` (NOT zipped, unlike
 * Microsoft Ads which mandates ZIP-CSV) — saving ~200 lines of unzip
 * machinery. We use `report_format: "JSON"` exclusively.
 *
 * Security:
 *   - The signed download URL contains a SAS-style signature (5-min
 *     validity) — never log it directly. All errors flow through the typed
 *     `PinterestAdsApiCallError` path which sanitises via `sanitizeErrorMessage`.
 *
 * @see https://developers.pinterest.com/docs/api/v5/analytics-create_report/
 * @see https://developers.pinterest.com/docs/analytics-and-reports/ads-reporting/
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { PINTEREST_API_BASE, buildPinterestHeaders } from "./api";
import { PinterestAdsApiCallError, parsePinterestAdsApiError } from "./errors";
import type {
  PinterestAdsGranularity,
  PinterestAttributionWindowDays,
} from "./analytics";

// ============================================================================
// Constants
// ============================================================================

export const PINTEREST_REPORTS_API_BASE = PINTEREST_API_BASE;

/** Initial poll interval. Backoff multiplies until POLL_INTERVAL_MS_MAX. */
export const POLL_INTERVAL_MS_INITIAL = 2_000;
/** Cap on per-attempt poll interval. */
export const POLL_INTERVAL_MS_MAX = 10_000;
/** Backoff growth factor between successive polls. */
export const POLL_INTERVAL_MULTIPLIER = 1.5;
/** Default poll-attempt cap for typical reports (~1 minute total budget). */
export const MAX_POLL_ATTEMPTS_SMALL = 30;
/** Extended poll-attempt cap for large pin-promotion-level reports (~5 min). */
export const MAX_POLL_ATTEMPTS_LARGE = 60;

export const CREATE_TIMEOUT_MS = 15_000;
export const POLL_TIMEOUT_MS = 10_000;
export const DOWNLOAD_TIMEOUT_MS = 30_000;

// ============================================================================
// Types
// ============================================================================

/**
 * Async report aggregation level. `ADVERTISER`/`CAMPAIGN`/`AD_GROUP`/`AD` are
 * also reachable via sync at lower column counts; the rest are async-only.
 */
export type PinterestAdsAsyncReportLevel =
  | "ADVERTISER"
  | "CAMPAIGN"
  | "AD_GROUP"
  | "AD"
  | "PIN_PROMOTION"
  | "PRODUCT_GROUP"
  | "PRODUCT_ITEM"
  | "KEYWORD";

export type PinterestAdsAsyncReportStatus =
  | "IN_PROGRESS"
  | "FINISHED"
  | "FAILED"
  | "EXPIRED"
  | "DOES_NOT_EXIST";

export interface PinterestAdsAsyncReportCreateBody {
  readonly start_date: string; // YYYY-MM-DD
  readonly end_date: string;   // YYYY-MM-DD
  readonly level: PinterestAdsAsyncReportLevel;
  readonly granularity: PinterestAdsGranularity;
  readonly columns: ReadonlyArray<string>;
  readonly click_window_days?: PinterestAttributionWindowDays;
  readonly engagement_window_days?: PinterestAttributionWindowDays;
  readonly view_window_days?: PinterestAttributionWindowDays;
  readonly conversion_report_time?: "TIME_OF_AD_ACTION" | "TIME_OF_CONVERSION";
  readonly attribution_types?: ReadonlyArray<"INDIVIDUAL" | "HOUSEHOLD">;
  readonly report_format?: "JSON" | "CSV";
  readonly campaign_ids?: ReadonlyArray<string>;
  readonly ad_group_ids?: ReadonlyArray<string>;
  readonly ad_ids?: ReadonlyArray<string>;
}

export interface PinterestAdsAsyncReportCreateResponse {
  readonly token: string;
}

export interface PinterestAdsAsyncReportPollResponse {
  readonly report_status: PinterestAdsAsyncReportStatus;
  readonly url?: string;
  readonly size?: number;
  readonly message?: string;
}

/** Levels Pinterest 400s on without `campaign_ids[]` (or deeper-scope IDs). */
const LEVELS_REQUIRING_CAMPAIGN_IDS: ReadonlySet<PinterestAdsAsyncReportLevel> =
  new Set(["CAMPAIGN", "AD_GROUP", "AD"]);

// ============================================================================
// Create
// ============================================================================

/**
 * POST /v5/ad_accounts/{accountId}/reports — submits an async report job.
 *
 * Returns the opaque `token` that callers feed into `pollPinterestAdsAsyncReport`.
 * Pre-validates body to avoid Pinterest 400s for missing scoping IDs.
 */
export async function createPinterestAdsAsyncReport(
  accessToken: string,
  accountId: string,
  body: PinterestAdsAsyncReportCreateBody,
): Promise<PinterestAdsAsyncReportCreateResponse> {
  if (
    LEVELS_REQUIRING_CAMPAIGN_IDS.has(body.level) &&
    (!body.campaign_ids || body.campaign_ids.length === 0)
  ) {
    throw new Error(
      `Pinterest Ads async report level "${body.level}" requires campaign_ids[]`,
    );
  }
  if (body.columns.length === 0) {
    throw new Error("Pinterest Ads async report requires at least one column");
  }

  const url = `${PINTEREST_REPORTS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/reports`;
  const result = await safeFetch(
    url,
    {
      method: "POST",
      headers: buildPinterestHeaders(accessToken),
      body: JSON.stringify({ report_format: "JSON", ...body }),
    },
    CREATE_TIMEOUT_MS,
  );
  if (!result.success) {
    throw buildReportsApiError(result.error, "report create");
  }
  let json: PinterestAdsAsyncReportCreateResponse;
  try {
    json = (await result.data.json()) as PinterestAdsAsyncReportCreateResponse;
  } catch {
    throw new PinterestAdsApiCallError(
      {
        type: "unknown",
        message: "Pinterest Ads report create response was not valid JSON",
        status: null,
        retryable: false,
      },
      "Pinterest Ads report create",
    );
  }
  if (!json.token || typeof json.token !== "string") {
    throw new PinterestAdsApiCallError(
      {
        type: "unknown",
        message: "Pinterest Ads report create response missing token",
        status: null,
        retryable: false,
      },
      "Pinterest Ads report create",
    );
  }
  return json;
}

// ============================================================================
// Poll (single attempt)
// ============================================================================

/**
 * GET /v5/ad_accounts/{accountId}/reports?token=… — single-attempt poll.
 * Callers wrap in `pollPinterestAdsAsyncReportUntilDone` for the backoff loop.
 */
export async function pollPinterestAdsAsyncReport(
  accessToken: string,
  accountId: string,
  token: string,
): Promise<PinterestAdsAsyncReportPollResponse> {
  if (!token) {
    throw new Error("Pinterest Ads report poll requires a token");
  }
  const url = new URL(
    `${PINTEREST_REPORTS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/reports`,
  );
  url.searchParams.set("token", token);

  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildPinterestHeaders(accessToken) },
    POLL_TIMEOUT_MS,
  );
  if (!result.success) {
    throw buildReportsApiError(result.error, "report poll");
  }
  try {
    return (await result.data.json()) as PinterestAdsAsyncReportPollResponse;
  } catch {
    throw new PinterestAdsApiCallError(
      {
        type: "unknown",
        message: "Pinterest Ads report poll response was not valid JSON",
        status: null,
        retryable: false,
      },
      "Pinterest Ads report poll",
    );
  }
}

// ============================================================================
// Poll (backoff loop until terminal status)
// ============================================================================

export interface PollUntilDoneOptions {
  readonly maxAttempts?: number;
  /** Test seam — defaults to `setTimeout`. */
  readonly sleepMs?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Polls until the report reaches a terminal status (FINISHED/FAILED/EXPIRED).
 * Returns the signed download URL on FINISHED. Throws typed
 * `PinterestAdsApiCallError` on FAILED/EXPIRED/poll-cap exhaustion.
 *
 * Backoff: 2s × 1.5^n capped at 10s. Default cap 30 attempts (~1 minute);
 * pin-promotion-level callers pass `MAX_POLL_ATTEMPTS_LARGE` for ~5 minutes.
 *
 * EXPIRED is surfaced as a typed error with `type: "unknown"` so the
 * adapter's stale-URL recovery branch (`runAsyncPipeline`) can catch it and
 * re-create the report once. We do NOT auto-recover here to keep this
 * function single-responsibility (poll, not orchestration).
 */
export async function pollPinterestAdsAsyncReportUntilDone(
  accessToken: string,
  accountId: string,
  token: string,
  options: PollUntilDoneOptions = {},
): Promise<string> {
  const maxAttempts = options.maxAttempts ?? MAX_POLL_ATTEMPTS_SMALL;
  const sleep = options.sleepMs ?? defaultSleep;

  let interval = POLL_INTERVAL_MS_INITIAL;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const response = await pollPinterestAdsAsyncReport(accessToken, accountId, token);
    if (response.report_status === "FINISHED") {
      if (!response.url) {
        throw new PinterestAdsApiCallError(
          {
            type: "unknown",
            message: "Pinterest Ads report FINISHED but no download URL was returned",
            status: null,
            retryable: false,
          },
          "Pinterest Ads report poll",
        );
      }
      return response.url;
    }
    if (response.report_status === "FAILED") {
      throw new PinterestAdsApiCallError(
        {
          type: "unknown",
          message: sanitizeErrorMessage(
            response.message
              ? `Pinterest Ads report FAILED: ${response.message}`
              : "Pinterest Ads report FAILED",
          ),
          status: null,
          retryable: false,
        },
        "Pinterest Ads report poll",
      );
    }
    if (response.report_status === "EXPIRED" || response.report_status === "DOES_NOT_EXIST") {
      throw new PinterestAdsApiCallError(
        {
          type: "unknown",
          message: `Pinterest Ads report ${response.report_status} — caller should re-create`,
          status: null,
          retryable: false,
        },
        "Pinterest Ads report poll",
      );
    }
    // IN_PROGRESS — wait and retry, unless this was the last attempt.
    if (attempt < maxAttempts) {
      await sleep(interval);
      interval = Math.min(Math.round(interval * POLL_INTERVAL_MULTIPLIER), POLL_INTERVAL_MS_MAX);
    }
  }
  // Exhausted poll budget — surface as rate_limited so the queue consumer
  // re-tries the whole `fetchMetrics` later (same path as Pinterest 429s).
  throw new PinterestAdsApiCallError(
    {
      type: "rate_limited",
      message: `Pinterest Ads report poll exhausted after ${maxAttempts} attempts`,
      status: null,
      retryable: true,
    },
    "Pinterest Ads report poll",
  );
}

// ============================================================================
// Download
// ============================================================================

/**
 * Downloads + JSON-parses the async report payload from the signed URL.
 *
 * The URL contains its own SAS-style signature; we MUST NOT add an
 * Authorization header (Pinterest signs against the URL alone). 5-minute
 * validity per Pinterest docs — callers should be ready to re-poll for a
 * fresh URL when this throws with the EXPIRED-style message.
 */
export async function downloadPinterestAdsAsyncReport(
  reportUrl: string,
): Promise<unknown> {
  if (!reportUrl) {
    throw new Error("Pinterest Ads report download requires a URL");
  }
  const result = await safeFetch(reportUrl, { method: "GET" }, DOWNLOAD_TIMEOUT_MS);
  if (!result.success) {
    throw buildReportsApiError(result.error, "report download");
  }
  try {
    return await result.data.json();
  } catch {
    throw new PinterestAdsApiCallError(
      {
        type: "unknown",
        message: "Pinterest Ads report download response was not valid JSON",
        status: null,
        retryable: false,
      },
      "Pinterest Ads report download",
    );
  }
}

// ============================================================================
// Internal: typed error builder
// ============================================================================

function buildReportsApiError(
  safeFetchError: { message: string; providerMessage?: string; providerStatus?: number },
  label: string,
): PinterestAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON
    }
  }
  const parsed = parsePinterestAdsApiError(parsedBody, safeFetchError.providerStatus);
  return new PinterestAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    `Pinterest Ads ${label}`,
  );
}
